import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends, objectSpread2 as _objectSpread2, typeof as _typeof } from '../../_virtual/_rollupPluginBabelHelpers.js';
import classNames from 'classnames';
import ResizeObserver from 'rc-resize-observer';
import React, { useRef, useState, useMemo, useCallback } from 'react';
import measure, { removeGhostContainer } from './util.esm.js';
import { isBrowser } from '../../utils/dom.esm.js';
import makeId from '../../utils/make-id.esm.js';
import wrapperRaf from '../../utils/raf-wrapper.esm.js';
import { mergeRefs } from '../../utils/react-helpers.esm.js';
import Article from '../_primitives/article/article.esm.js';
import TextFormatter from '../text-formatter/text-formatter.esm.js';
import Expander from './expander.esm.js';
import { ELLIPSIS_STR } from '../text-formatter/utils.esm.js';
import useUnmount from '../../hooks/use-unmount.esm.js';

var _excluded = ["id", "as", "className", "columnGap", "columns", "children", "ellipsis"];
function getEllipsis(ellipsis) {
  if (!ellipsis) return {};
  return _objectSpread2({
    rows: 1,
    expandable: false
  }, _typeof(ellipsis) === 'object' ? ellipsis : null);
}
var initialState = {
  ellipsisText: '',
  ellipsisContent: null,
  isEllipsis: false
};
var TextContainer = /*#__PURE__*/React.forwardRef(function TextContainer(props, ref) {
  var id = props.id,
    _props$as = props.as,
    asProps = _props$as === void 0 ? 'p' : _props$as,
    className = props.className,
    columnGap = props.columnGap,
    columns = props.columns,
    children = props.children,
    ellipsis = props.ellipsis,
    rest = _objectWithoutProperties(props, _excluded);
  var identifierRef = useRef(id || makeId());
  var contentRef = useRef(null);
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    rafId = _useState2[0],
    setRafId = _useState2[1];
  var ellipsisOptions = useMemo(function () {
    return getEllipsis(ellipsis);
  }, [ellipsis]);
  var _useState3 = useState(initialState),
    _useState4 = _slicedToArray(_useState3, 2),
    ellipsisState = _useState4[0],
    setEllipsisState = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    expanded = _useState6[0],
    setExpanded = _useState6[1];
  var ellipsisText = ellipsisState.ellipsisText,
    ellipsisContent = ellipsisState.ellipsisContent,
    isEllipsis = ellipsisState.isEllipsis;
  var rows = ellipsisOptions.rows,
    suffix = ellipsisOptions.suffix,
    onExpand = ellipsisOptions.onExpand,
    expandable = ellipsisOptions.expandable;
  var handleOnExpandClick = useCallback(function () {
    if (expandable && !onExpand) {
      setExpanded(!expanded);
    }
    if (onExpand) {
      onExpand();
    }
  }, [expandable, expanded, onExpand]);
  var renderExpand = useCallback(function () {
    if (!expandable) return null;
    return /*#__PURE__*/React.createElement(Expander, {
      key: "text-container-expander-".concat(identifierRef.current),
      className: "csu-tui-text-container-expander",
      position: "absolute",
      right: expanded ? '0' : '0.75rem',
      bottom: expanded ? '-0.6rem' : '0.1rem',
      "aria-label": expanded ? 'click for less text' : 'click for more text',
      onClick: handleOnExpandClick
    }, expanded ? 'less' : 'more');
  }, [expandable, expanded, handleOnExpandClick]);
  var renderOperations = useCallback(function () {
    return [renderExpand()].filter(function (node) {
      return node;
    });
  }, [renderExpand]);
  var syncEllipsis = useCallback(function () {
    // @ts-ignore
    if (!rows || rows < 0 || !contentRef.current || expanded) return;

    // Do not measure if not browser render
    if (!isBrowser) return;
    var _measure = measure(identifierRef.current,
      // @ts-ignore
      contentRef.current, {
        rows: rows,
        suffix: suffix
      }, children, renderOperations(), ELLIPSIS_STR),
      content = _measure.content,
      text = _measure.text,
      ellipsis = _measure.ellipsis;
    if (ellipsisText !== text || isEllipsis !== ellipsis) {
      setEllipsisState({
        ellipsisText: text,
        ellipsisContent: content,
        isEllipsis: ellipsis
      });
    }
  }, [children, contentRef, ellipsisText, expanded, isEllipsis, renderOperations, rows, suffix]);
  var handleResize = useCallback(function () {
    wrapperRaf.cancel(rafId);
    setRafId(wrapperRaf(syncEllipsis));
  }, [rafId, syncEllipsis]);
  useUnmount(function () {
    removeGhostContainer(identifierRef.current);
  });
  return /*#__PURE__*/React.createElement(ResizeObserver, {
    onResize: handleResize
  }, /*#__PURE__*/React.createElement(Article, _extends({
    className: classNames({
      'csg-tui-article': true
    }, className),
    ref: mergeRefs(ref, contentRef),
    position: expanded ? 'relative' : undefined
  }, _objectSpread2({
    columnGap: columnGap,
    columns: columns
  }, rest)), /*#__PURE__*/React.createElement(TextFormatter, {
    as: asProps,
    id: identifierRef.current
  }, "".concat(isEllipsis && !expanded ? ellipsisContent : children), "".concat(isEllipsis && !expanded ? ELLIPSIS_STR : ''), "".concat(suffix ? suffix : ''), isEllipsis && !expanded && renderOperations()), isEllipsis && expanded && renderOperations()));
});

export default TextContainer;
//# sourceMappingURL=text-container.esm.js.map
